import { useState, useEffect } from "react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Lock, Save } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useMutation } from "@tanstack/react-query";

import { AdminVerificationDialog } from "@/components/admin-components/AdminVerificationDialog";
import { makeRequest } from "@/utils/makeRequest";
import Loader from "@/components/loader/Loader";
import { useCurrencies, useProfile, useSystemSettings } from "@/hooks";
import { useAppSelector } from "@/store/hooks";
import { ErrorState } from "@/components/ErrorState";
import { CustomSelect } from "@/components/shared/CustomSelect";
import type { SingleValue } from "react-select";
import type { Currency } from "@/interfaces/currency";
import { removeTrailingToken } from "@/utils";
import TransactionPasswordSetting from "@/components/shared/TransactionPasswordSetting";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { queryClient } from "@/services/queryClient";

interface SettingsSectionProps {
  title: string;
  children: React.ReactNode;
}

const SettingsSection = ({ title, children }: SettingsSectionProps) => (
  <div className="bg-card border border-border rounded-xl overflow-hidden mb-6">
    <div className="p-4 border-b border-border">
      <h3 className="font-semibold">{title}</h3>
    </div>
    <div className="p-4 space-y-4">{children}</div>
    <div className="p-4 border-t border-border bg-muted/50 flex justify-end"></div>
  </div>
);

const SectionContent = ({
  disabled,
  children,
}: {
  disabled?: boolean;
  children: React.ReactNode;
}) => (
  <div
    className={cn(
      "space-y-4 transition-all duration-200",
      disabled && "opacity-50 pointer-events-none select-none",
    )}
    aria-disabled={disabled}
  >
    {children}
  </div>
);

const ToggleField = ({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) => (
  <label className="flex items-center justify-between p-4 bg-muted rounded-xl cursor-pointer">
    <div>
      <p className="font-medium">{label}</p>
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
        checked ? "bg-yellow-500" : "bg-muted-foreground/30",
      )}
    >
      <span
        className={cn(
          "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
          checked ? "translate-x-6" : "translate-x-1",
        )}
      />
    </button>
  </label>
);

const NumberField = ({
  label,
  value,
  onChange,
  suffix,
  disabled,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  suffix?: string;
  disabled?: boolean;
}) => (
  <div>
    <label className="text-sm font-medium mb-2 block">{label}</label>

    <div className="relative">
      <input
        type="number"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className={cn(
          "w-full px-4 py-3 bg-muted border border-border rounded-xl transition-all",
          disabled && "cursor-not-allowed opacity-60",
        )}
      />

      {suffix && (
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">
          {suffix}
        </span>
      )}
    </div>
  </div>
);

export default function AdminSettings() {
  const [mode, setMode] = useState("");
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");

  const [showVerification, setShowVerification] = useState(false);
  const [loading, setLoading] = useState(false);

  const [verificationType, setVerificationType] = useState<
    "profile" | "admin-settings" | "security" | "notification" | null
  >(null);

  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id || "";
  const { data: userProfile } = useProfile(userId);

  const {
    data: currencies,
    isLoading: isLoadingCurrency,
    isError: isErrorCurrency,
    refetch: refetchCurrencies,
  } = useCurrencies();

  const {
    data: adminSettings,
    isLoading,
    isError,
    refetch,
  } = useSystemSettings();

  console.log("ADMIN SETTINGS", adminSettings);

  const handleCurrency = (
    option: SingleValue<{ value: string; label: string }>,
  ) => {
    setGeneral((prev: any) => ({
      ...prev,
      currencyId: option?.value ?? null,
    }));
  };

  const options =
    currencies?.map((currency: Currency) => {
      return {
        value: currency.id,
        label:
          removeTrailingToken(currency.name) +
          " " +
          "(" +
          currency.symbol +
          ")",
        iso: currency.iso,
        iso2: currency.iso2,
      };
    }) ?? [];

  const formatOptionLabel = (option: any) => {
    const iso2 = option.iso2?.toLowerCase();

    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <img
          src={`https://flagcdn.com/w40/${iso2}.png`}
          alt={option.label}
          width={20}
          height={14}
          style={{ objectFit: "cover", borderRadius: 2 }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
        <span>{option.label}</span>
      </div>
    );
  };

  const [ecoSettings, setEcoSettings] = useState({
    price: 0.5,
  });

  // Profile state
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    middleName: "",
    email: "",
    phoneNumber: "",
  });

  //Profile security settings state
  const [security, setSecurity] = useState({
    twoFactor: true,
    loginNotifications: true,
    sessionTimeout: true,
  });

  //Profile notification settings state
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    securityAlerts: true,
    tradeAlerts: false,
    marketing: false,
  });

  useEffect(() => {
    if (!userProfile || !userProfile?.setting) return;

    setProfile({
      firstName: userProfile?.firstName,
      lastName: userProfile?.lastName,
      middleName: userProfile?.middleName || "",
      email: userProfile?.email,
      phoneNumber: userProfile?.phoneNumber || "",
    });

    setSecurity({
      twoFactor: userProfile?.setting?.twoFAEnabled,
      loginNotifications: userProfile?.setting?.pushNotifications,
      sessionTimeout: true,
      // sessionTimeout: userProfile?.setting?.passwordExpiry||30,
    });

    setNotifications({
      email: userProfile?.setting?.emailAlerts,
      push: userProfile?.setting?.pushNotifications,
      securityAlerts: true,
      tradeAlerts: true,
      marketing: userProfile?.setting?.newFeatureAlert,
    });
  }, [userProfile, userProfile?.setting]);

  const profileBuildPayload = () => ({
    firstName: profile.firstName,
    lastName: profile.lastName,
    middleName: profile.middleName,
    email: profile.email,
    phoneNumber: profile?.phoneNumber,
  });

  // Platform settings state
  const [platform, setPlatform] = useState({
    maintenanceMode: false,
    newUserRegistration: true,
    emailVerification: true,
    kycRequired: true,
  });

  // General settings
  const [general, setGeneral] = useState({
    platformName: "BuyFiat",
    maintenanceMode: false,
    currencyId: null as string | null,
    platformEmailVerification: true,
    platformKycRequired: true,
    platformNewUserRegistration: true,
  });

  // Withdrwal fee settings
  const [depositSetting, setDepositSetting] = useState({
    depositMinUSD: 10,
    depositMaxUSD: 100000,
  });

  // Withdrwal fee settings
  const [withdrawalFee, setWithdrawalFee] = useState({
    withdrawalFeePercent: 1.5,
    withdrawalFeeCap: 100,
    withdrawalFeeEnabled: true,
    description: "",
  });

  // Transfer fee settings
  const [transferFee, setTransferFee] = useState({
    transferFeePercent: 0.5,
    transferFeeCap: 100,
    transferFeeEnabled: true,
    description: "",
  });

  // P2P fee settings
  const [p2pFee, setp2pFee] = useState({
    p2pFeePercent: 1.2,
    p2pFeeCap: 100,
    p2pFeeEnabled: true,
    description: "",
  });

  // Swap fee settings
  const [swapFee, setSwapFee] = useState({
    swapFeePercent: 1.0,
    swapFeeCap: 100,
    swapFeeEnabled: true,
    description: "",
  });

  // Trading settings
  const [trading, setTrading] = useState({
    minTrade: 10,
    maxTrade: 100000,
    paymentWindow: 30,
    escrowRelease: 1440,
  });

  // Security settings
  const [newSecurity, setNewSecurity] = useState({
    require2FA: true,
    maxLoginAttempts: 5,
    lockDuration: 30,

    securityLoginNotifications: true,
    securitySessionTimeout: true,
  });

  // KYC settings
  const [kyc, setKyc] = useState({
    requiredForTrading: true,
    requiredForWithdrawal: true,
    autoApprove: false,
    requiredForSwap: false,
    requiredForTransfer: false,
  });

  // System notifications
  const [systemNotification, setSystemNotification] = useState({
    notificationsEmail: true,
    notificationsMarketing: true,
    notificationsPush: false,
    notificationsSecurityAlerts: false,
    notificationsTradeAlerts: false,
  });

  useEffect(() => {
    if (!adminSettings) return;

    setEcoSettings({
      price: Number(adminSettings.ECO_PRICE),
    });

    setGeneral({
      platformName: adminSettings.generalPlatformName,
      currencyId: adminSettings.currency?.id || null,
      maintenanceMode: adminSettings.generalMaintenanceMode,
      platformEmailVerification: adminSettings.platformEmailVerification,
      platformKycRequired: adminSettings.platformKycRequired,
      platformNewUserRegistration: adminSettings.platformNewUserRegistration,
    });

    setDepositSetting({
      depositMinUSD: Number(adminSettings.depositMinUSD),
      depositMaxUSD: Number(adminSettings.depositMaxUSD),
    });

    setWithdrawalFee({
      withdrawalFeePercent: Number(adminSettings.withdrawalFeePercent),
      withdrawalFeeCap: Number(adminSettings.withdrawalFeeCap),
      withdrawalFeeEnabled: adminSettings.withdrawalFeeEnabled,
      description: adminSettings.withdrawalFeeDescription || "",
    });

    setTransferFee({
      transferFeePercent: Number(adminSettings.transferFeePercent),
      transferFeeCap: Number(adminSettings.transferFeeCap),
      transferFeeEnabled: adminSettings.transferFeeEnabled,
      description: adminSettings.transferFeeDescription || "",
    });

    setp2pFee({
      p2pFeePercent: Number(adminSettings.p2pFeePercent),
      p2pFeeCap: Number(adminSettings.p2pFeeCap),
      p2pFeeEnabled: adminSettings.p2pFeeEnabled,
      description: adminSettings.p2pFeeDescription || "",
    });

    setSwapFee({
      swapFeePercent: Number(adminSettings.swapFeePercent),
      swapFeeCap: Number(adminSettings.swapFeeCap),
      swapFeeEnabled: adminSettings.swapFeeEnabled,
      description: adminSettings.swapFeeDescription || "",
    });

    setTrading({
      minTrade: adminSettings.tradingMinTrade,
      maxTrade: adminSettings.tradingMaxTrade,
      paymentWindow: adminSettings.tradingPaymentWindow,
      escrowRelease: adminSettings.tradingEscrowRelease,
    });

    setNewSecurity({
      require2FA: adminSettings.securityRequire2FA,
      maxLoginAttempts: adminSettings.securityMaxLoginAttempts,
      lockDuration: adminSettings.securityLockDuration,
      securityLoginNotifications: adminSettings.securityLoginNotifications,
      securitySessionTimeout: adminSettings.securitySessionTimeout,
    });

    setKyc({
      requiredForTrading: adminSettings.kycRequiredForTrading,
      requiredForWithdrawal: adminSettings.kycRequiredForWithdrawal,
      autoApprove: adminSettings.kycAutoApprove,
      requiredForSwap: adminSettings.kycRequiredForSwap,
      requiredForTransfer: adminSettings.kycRequiredForTransfer,
    });

    setSystemNotification({
      notificationsEmail: adminSettings.notificationsEmail,
      notificationsMarketing: adminSettings.notificationsMarketing,
      notificationsPush: adminSettings.notificationsPush,
      notificationsSecurityAlerts: adminSettings.notificationsSecurityAlerts,
      notificationsTradeAlerts: adminSettings.notificationsTradeAlerts,
    });
  }, [adminSettings]);

  const buildPayload = () => ({
    id: adminSettings?.id,
    generalPlatformName: general.platformName,
    currencyId: general.currencyId,
    generalMaintenanceMode: general.maintenanceMode,

    platformEmailVerification: general.platformEmailVerification,
    platformKycRequired: general.platformKycRequired,
    platformNewUserRegistration: general.platformNewUserRegistration,

    depositMinUSD: depositSetting.depositMinUSD,
    depositMaxUSD: depositSetting.depositMaxUSD,

    withdrawalFeePercent: withdrawalFee.withdrawalFeePercent,
    withdrawalFeeCap: withdrawalFee.withdrawalFeeCap,
    withdrawalFeeEnabled: withdrawalFee.withdrawalFeeEnabled,

    transferFeePercent: transferFee.transferFeePercent,
    transferFeeCap: transferFee.transferFeeCap,
    transferFeeEnabled: transferFee.transferFeeEnabled,

    p2pFeePercent: p2pFee.p2pFeePercent,
    p2pFeeCap: p2pFee.p2pFeeCap,
    p2pFeeEnabled: p2pFee.p2pFeeEnabled,

    swapFeePercent: swapFee.swapFeePercent,
    swapFeeCap: swapFee.swapFeeCap,
    swapFeeEnabled: swapFee.swapFeeEnabled,

    tradingMinTrade: trading.minTrade,
    tradingMaxTrade: trading.maxTrade,
    tradingPaymentWindow: trading.paymentWindow,
    tradingEscrowRelease: trading.escrowRelease,

    securityRequire2FA: newSecurity.require2FA,
    securityMaxLoginAttempts: newSecurity.maxLoginAttempts,
    securityLockDuration: newSecurity.lockDuration,
    securityLoginNotifications: newSecurity.securityLoginNotifications,
    securitySessionTimeout: newSecurity.securitySessionTimeout,

    kycRequiredForTrading: kyc.requiredForTrading,
    kycRequiredForWithdrawal: kyc.requiredForWithdrawal,
    kycAutoApprove: kyc.autoApprove,
    kycRequiredForSwap: kyc.requiredForSwap,
    kycRequiredForTransfer: kyc.requiredForTransfer,

    notificationsEmail: systemNotification.notificationsEmail,
    notificationsMarketing: systemNotification.notificationsMarketing,
    notificationsPush: systemNotification.notificationsPush,
    notificationsSecurityAlerts: systemNotification.notificationsSecurityAlerts,
    notificationsTradeAlerts: systemNotification.notificationsTradeAlerts,

    ECO_PRICE: ecoSettings.price,
  });

  const updateSettings = async (payload: any) => {
    const res = await makeRequest.put("/admin-setting", payload);
    return res.data;
  };

  const mutation = useMutation({
    mutationFn: updateSettings,

    onSuccess: () => {
      toast.success("Settings updated successfully");
    },

    onError: (error) => {
      toast.error(getErrorMessage(error));
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-settings"],
      });
    },
  });

  // const mutation = useMutation({
  //   mutationFn: updateSettings,

  //   onMutate: async (newData) => {
  //     await queryClient.cancelQueries({ queryKey: ["admin-settings"] });

  //     const previous = queryClient.getQueryData(["admin-settings"]);

  //     queryClient.setQueryData(["admin-settings"], (old: any) => ({
  //       ...old,
  //       ...newData,
  //     }));

  //     return { previous };
  //   },

  //   onError: (_err, _newData, context) => {
  //     if (context?.previous) {
  //       queryClient.setQueryData(["admin-settings"], context.previous);
  //     }

  //     toast.error(getErrorMessage(_err));
  //     console.log("error", _err);
  //   },

  //   onSettled: () => {
  //     queryClient.invalidateQueries({ queryKey: ["admin-settings"] });
  //     // toast.success("Admin settings updated");
  //   },
  // });

  const handleAlchemyWalletReset = async () => {
    setLoading(true);
    try {
      const res = await makeRequest.delete("/webhooks/alchemy/addresses");

      if (res.status === 200) {
        toast.success("Alchemy wallet addresses successfully cleared");
      }

      setLoading(false);

      console.log(res);
    } catch (error) {
      setLoading(false);

      toast.error("Error resetting alchemy wallet addresses");
    }
  };

  if (isError || isErrorCurrency)
    return (
      <ErrorState
        onRetry={() => {
          void refetch();
          void refetchCurrencies();
        }}
      />
    );
  if (isLoading || isLoadingCurrency) return <Loader type="bounce" />;

  return (
    <div className="flex flex-col gap-10">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              Admin Settings
            </h1>
            <p className="text-muted-foreground">
              Manage your profile and global platform settings
            </p>
          </div>
          <Button disabled={loading} onClick={handleAlchemyWalletReset}>
            {loading ? "Processing..." : " Reset Alchemy Wallets"}
          </Button>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="space-y-4"
        >
          <TabsList className="flex items-center justify-between gap-4 w-full">
            <TabsTrigger value="profile">Profile</TabsTrigger>

            <TabsTrigger value="platform">Platform</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>
                  Update your personal information
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      value={profile.firstName}
                      onChange={(e) =>
                        setProfile({ ...profile, firstName: e.target.value })
                      }
                      className="!h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      value={profile.lastName}
                      onChange={(e) =>
                        setProfile({ ...profile, lastName: e.target.value })
                      }
                      className="!h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="middleName">Middle Name</Label>
                    <Input
                      id="middleName"
                      value={profile.middleName}
                      onChange={(e) =>
                        setProfile({ ...profile, middleName: e.target.value })
                      }
                      className="!h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      value={profile.email}
                      disabled
                      className="!h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phoneNumber">Phone</Label>
                    <Input
                      id="phoneNumber"
                      value={profile.phoneNumber}
                      onChange={(e) =>
                        setProfile({ ...profile, phoneNumber: e.target.value })
                      }
                      className="!h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Input
                      id="role"
                      value="Super Administrator"
                      disabled
                      className="!h-11"
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={() => {
                      setVerificationType("profile");
                      setShowVerification(true);
                    }}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              </CardContent>

              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-primary" />
                  </div>

                  <div>
                    <p className="text-foreground font-medium">
                      Transaction Password
                    </p>
                    <span className="text-sm text-gray-500">
                      Required for transaction authorization
                    </span>
                  </div>
                </div>

                <Button
                  onClick={() => {
                    setMode(
                      userProfile?.isSecurityCodeVerified ? "update" : "create",
                    );
                    setOpen(true);
                  }}
                >
                  {userProfile?.isSecurityCodeVerified ? "Update" : "Setup"}
                </Button>
              </div>
            </Card>

            {/* ✅ SECURITY SETTINGS (UPDATED TO ToggleField) */}
            <Card>
              <CardHeader>
                <CardTitle>Security Settings</CardTitle>
                <CardDescription>
                  Manage your security preferences
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* <SettingsSection title="Security Code">
                  <SecurityCodeSection />
                </SettingsSection> */}

                <SettingsSection title="Security Preferences">
                  <ToggleField
                    label="Enable Two-Factor Authentication"
                    description="Add an extra layer of protection to your account"
                    checked={security.twoFactor}
                    onChange={(v) => setSecurity({ ...security, twoFactor: v })}
                  />

                  <ToggleField
                    label="Login Notifications"
                    description="Receive alerts when your account is accessed"
                    checked={security.loginNotifications}
                    onChange={(v) =>
                      setSecurity({ ...security, loginNotifications: v })
                    }
                  />

                  <ToggleField
                    label="Session Timeout"
                    description="Automatically log out after inactivity"
                    checked={security.sessionTimeout}
                    onChange={(v) =>
                      setSecurity({ ...security, sessionTimeout: v })
                    }
                  />
                </SettingsSection>

                <div className="flex justify-end">
                  <Button
                    onClick={() => {
                      setVerificationType("security");
                      setShowVerification(true);
                    }}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Security Settings
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* ✅ NOTIFICATIONS */}
            <Card>
              <CardHeader>
                <CardTitle>Notification Preferences</CardTitle>
                <CardDescription>
                  Choose what notifications you receive
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                <SettingsSection title="Notifications">
                  <ToggleField
                    label="Email Notifications"
                    checked={notifications.email}
                    onChange={(v) =>
                      setNotifications({ ...notifications, email: v })
                    }
                  />

                  <ToggleField
                    label="Push Notifications"
                    checked={notifications.push}
                    onChange={(v) =>
                      setNotifications({ ...notifications, push: v })
                    }
                  />

                  <ToggleField
                    label="Security Alerts"
                    checked={notifications.securityAlerts}
                    onChange={(v) =>
                      setNotifications({
                        ...notifications,
                        securityAlerts: v,
                      })
                    }
                  />

                  <ToggleField
                    label="Trade Alerts"
                    checked={notifications.tradeAlerts}
                    onChange={(v) =>
                      setNotifications({
                        ...notifications,
                        tradeAlerts: v,
                      })
                    }
                  />

                  <ToggleField
                    label="Marketing Emails"
                    checked={notifications.marketing}
                    onChange={(v) =>
                      setNotifications({
                        ...notifications,
                        marketing: v,
                      })
                    }
                  />
                </SettingsSection>

                <div className="flex justify-end">
                  <Button
                    onClick={() => {
                      setVerificationType("notification");
                      setShowVerification(true);
                    }}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Notification Preferences
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PLATFORM CONFIGURATIONS */}
          <TabsContent value="platform" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Platform Settings</CardTitle>
                <CardDescription>
                  Configure global platform settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* General */}
                <SettingsSection title="General">
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Platform Name
                    </label>
                    <input
                      type="text"
                      value={general.platformName}
                      onChange={(e) =>
                        setGeneral({ ...general, platformName: e.target.value })
                      }
                      className="w-full px-4 py-3 bg-muted border border-border rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Default Currency
                    </label>

                    <CustomSelect
                      options={options}
                      isClearable
                      isSearchable
                      value={
                        options.find(
                          (opt: any) => opt.value === general.currencyId,
                        ) || null
                      }
                      onChange={handleCurrency}
                      formatOptionLabel={formatOptionLabel}
                      getOptionLabel={(option: any) => option.label}
                      getOptionValue={(option: any) => option.value}
                    />
                    {/* <CustomSelect
                      options={options}
                      isClearable
                      isSearchable
                      value={
                        options.find(
                          (opt: any) => opt.value === general.currencyId,
                        ) || null
                      }
                      onChange={(
                        option: SingleValue<{ value: string; label: string }>,
                      ) =>
                        setGeneral({
                          ...general,
                          currencyId: option
                            ? option.value
                            : general.currencyId,
                        })
                      }
                    /> */}
                  </div>
                  <ToggleField
                    label="Maintenance Mode"
                    description="Put platform in maintenance mode"
                    checked={general.maintenanceMode}
                    onChange={(v) =>
                      setGeneral({ ...general, maintenanceMode: v })
                    }
                  />
                  <ToggleField
                    label="Enable Email Verification"
                    description="Enforce email verification for all users"
                    checked={general.platformEmailVerification}
                    onChange={(v) =>
                      setGeneral({ ...general, platformEmailVerification: v })
                    }
                  />
                  <ToggleField
                    label="Enable KYC"
                    description="Put platform in maintenance modeEnforce KYC for all users"
                    checked={general.platformKycRequired}
                    onChange={(v) =>
                      setGeneral({ ...general, platformKycRequired: v })
                    }
                  />
                  <ToggleField
                    label="Enable New User Registration"
                    description="Enforce registration for new users"
                    checked={general.platformNewUserRegistration}
                    onChange={(v) =>
                      setGeneral({ ...general, platformNewUserRegistration: v })
                    }
                  />
                </SettingsSection>

                <SettingsSection title="ECO Presale Configuration">
                  <NumberField
                    label="ECO Presale Price (USD)"
                    value={ecoSettings.price}
                    onChange={(v) =>
                      setEcoSettings({
                        ...ecoSettings,
                        price: v,
                      })
                    }
                    suffix="USD"
                  />
                </SettingsSection>

                {/*Deposit settings */}
                <SettingsSection title="Deposit Setting">
                  {/* <ToggleField
                    label="Enable Withdrawal Fee"
                    description="Turn withdrawal fee on/off globally"
                    checked={withdrawalFee.withdrawalFeeEnabled}
                    onChange={(v) =>
                      setWithdrawalFee({
                        ...withdrawalFee,
                        withdrawalFeeEnabled: v,
                      })
                    }
                  /> */}
                  <SectionContent
                    disabled={!withdrawalFee.withdrawalFeeEnabled}
                  >
                    <div className="grid sm:grid-cols-2 gap-4">
                      <NumberField
                        label="Min Deposit"
                        value={depositSetting.depositMinUSD}
                        onChange={(v) =>
                          setDepositSetting({
                            ...depositSetting,
                            depositMinUSD: v,
                          })
                        }
                        // suffix="%"
                        // disabled={!withdrawalFee.withdrawalFeeEnabled}
                      />

                      <NumberField
                        label="Maximim Deposit"
                        value={depositSetting.depositMaxUSD}
                        onChange={(v) =>
                          setDepositSetting({
                            ...depositSetting,
                            depositMaxUSD: v,
                          })
                        }
                        // disabled={!withdrawalFee.withdrawalFeeEnabled}
                      />
                    </div>
                  </SectionContent>
                </SettingsSection>

                {/*Withdrawal Fees */}
                <SettingsSection title="Withdrawal Fee">
                  <ToggleField
                    label="Enable Withdrawal Fee"
                    description="Turn withdrawal fee on/off globally"
                    checked={withdrawalFee.withdrawalFeeEnabled}
                    onChange={(v) =>
                      setWithdrawalFee({
                        ...withdrawalFee,
                        withdrawalFeeEnabled: v,
                      })
                    }
                  />
                  <SectionContent
                    disabled={!withdrawalFee.withdrawalFeeEnabled}
                  >
                    <div className="grid sm:grid-cols-2 gap-4">
                      <NumberField
                        label="Withdrawal Fee (%)"
                        value={withdrawalFee.withdrawalFeePercent}
                        onChange={(v) =>
                          setWithdrawalFee({
                            ...withdrawalFee,
                            withdrawalFeePercent: v,
                          })
                        }
                        suffix="%"
                        disabled={!withdrawalFee.withdrawalFeeEnabled}
                      />

                      <NumberField
                        label="Withdrawal Fee Cap"
                        value={withdrawalFee.withdrawalFeeCap}
                        onChange={(v) =>
                          setWithdrawalFee({
                            ...withdrawalFee,
                            withdrawalFeeCap: v,
                          })
                        }
                        disabled={!withdrawalFee.withdrawalFeeEnabled}
                      />
                    </div>
                  </SectionContent>
                </SettingsSection>

                {/*Transfer Fees */}
                <SettingsSection title="Transfer Fee">
                  <ToggleField
                    label="Enable Transfer Fee"
                    description="Turn transfer fee on/off globally"
                    checked={transferFee.transferFeeEnabled}
                    onChange={(v) =>
                      setTransferFee({ ...transferFee, transferFeeEnabled: v })
                    }
                  />
                  <SectionContent disabled={!transferFee.transferFeeEnabled}>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <NumberField
                        label="Transfer Fee (%)"
                        value={transferFee.transferFeePercent}
                        onChange={(v) =>
                          setTransferFee({
                            ...transferFee,
                            transferFeePercent: v,
                          })
                        }
                        suffix="%"
                      />
                      <NumberField
                        label="Transfer Fee Cap"
                        value={transferFee.transferFeeCap}
                        onChange={(v) =>
                          setTransferFee({ ...transferFee, transferFeeCap: v })
                        }
                      />
                    </div>
                  </SectionContent>
                </SettingsSection>

                {/*P2P Fees */}
                <SettingsSection title="P2P Fee">
                  <ToggleField
                    label="Enable P2P Fee"
                    description="Turn P2P fee on/off globally"
                    checked={p2pFee.p2pFeeEnabled}
                    onChange={(v) => setp2pFee({ ...p2pFee, p2pFeeEnabled: v })}
                  />
                  <SectionContent disabled={!p2pFee.p2pFeeEnabled}>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <NumberField
                        label="P2P Fee (%)"
                        value={p2pFee.p2pFeePercent}
                        onChange={(v) =>
                          setp2pFee({ ...p2pFee, p2pFeePercent: v })
                        }
                        suffix="%"
                      />
                      <NumberField
                        label="P2P Fee Cap"
                        value={p2pFee.p2pFeeCap}
                        onChange={(v) => setp2pFee({ ...p2pFee, p2pFeeCap: v })}
                      />
                    </div>
                  </SectionContent>
                </SettingsSection>

                {/*Swap Fees */}
                <SettingsSection title="Swap Fee">
                  <ToggleField
                    label="Enable Swap Fee"
                    description="Turn Swap fee on/off globally"
                    checked={swapFee.swapFeeEnabled}
                    onChange={(v) =>
                      setSwapFee({ ...swapFee, swapFeeEnabled: v })
                    }
                  />
                  <SectionContent disabled={!swapFee.swapFeeEnabled}>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <NumberField
                        label="Swap Fee (%)"
                        value={swapFee.swapFeePercent}
                        onChange={(v) =>
                          setSwapFee({ ...swapFee, swapFeePercent: v })
                        }
                        suffix="%"
                      />
                      <NumberField
                        label="Swap Fee Cap"
                        value={swapFee.swapFeeCap}
                        onChange={(v) =>
                          setSwapFee({ ...swapFee, swapFeeCap: v })
                        }
                      />
                    </div>
                  </SectionContent>
                </SettingsSection>

                {/* Trading */}
                <SettingsSection title="Trading">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <NumberField
                      label="Min Trade Amount"
                      value={trading.minTrade}
                      onChange={(v) => setTrading({ ...trading, minTrade: v })}
                    />
                    <NumberField
                      label="Max Trade Amount"
                      value={trading.maxTrade}
                      onChange={(v) => setTrading({ ...trading, maxTrade: v })}
                    />
                    <NumberField
                      label="Payment Window (min)"
                      value={trading.paymentWindow}
                      onChange={(v) =>
                        setTrading({ ...trading, paymentWindow: v })
                      }
                    />
                    <NumberField
                      label="Escrow Release (min)"
                      value={trading.escrowRelease}
                      onChange={(v) =>
                        setTrading({ ...trading, escrowRelease: v })
                      }
                    />
                  </div>
                </SettingsSection>

                {/* Security */}
                <SettingsSection title="Security">
                  <ToggleField
                    label="Require 2FA for Withdrawals"
                    checked={newSecurity.require2FA}
                    onChange={(v) =>
                      setNewSecurity({ ...newSecurity, require2FA: v })
                    }
                  />
                  <ToggleField
                    label="Allow Login Notifications"
                    checked={newSecurity.securityLoginNotifications}
                    onChange={(v) =>
                      setNewSecurity({
                        ...newSecurity,
                        securityLoginNotifications: v,
                      })
                    }
                  />
                  <ToggleField
                    label="Allow Session Timeout"
                    checked={newSecurity.securitySessionTimeout}
                    onChange={(v) =>
                      setNewSecurity({
                        ...newSecurity,
                        securitySessionTimeout: v,
                      })
                    }
                  />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <NumberField
                      label="Max Login Attempts"
                      value={newSecurity.maxLoginAttempts}
                      onChange={(v) =>
                        setNewSecurity({ ...newSecurity, maxLoginAttempts: v })
                      }
                    />
                    <NumberField
                      label="Lock Duration (min)"
                      value={newSecurity.lockDuration}
                      onChange={(v) =>
                        setNewSecurity({ ...newSecurity, lockDuration: v })
                      }
                    />
                  </div>
                </SettingsSection>

                {/* NOTIFICATIONS */}
                <SettingsSection title="NOTIFICATIONS">
                  <ToggleField
                    label="Required for Email notification"
                    checked={systemNotification.notificationsEmail}
                    onChange={(v) =>
                      setSystemNotification({
                        ...systemNotification,
                        notificationsEmail: v,
                      })
                    }
                  />
                  <ToggleField
                    label="Required for Marketing"
                    checked={systemNotification.notificationsMarketing}
                    onChange={(v) =>
                      setSystemNotification({
                        ...systemNotification,
                        notificationsMarketing: v,
                      })
                    }
                  />
                  <ToggleField
                    label="Enable Push Notifications"
                    description="Enable Push Notifications"
                    checked={systemNotification.notificationsPush}
                    onChange={(v) =>
                      setSystemNotification({
                        ...systemNotification,
                        notificationsPush: v,
                      })
                    }
                  />
                  <ToggleField
                    label="Required for Security alerts"
                    checked={systemNotification.notificationsSecurityAlerts}
                    onChange={(v) =>
                      setSystemNotification({
                        ...systemNotification,
                        notificationsSecurityAlerts: v,
                      })
                    }
                  />
                  <ToggleField
                    label="Required for Transfer alerts"
                    checked={systemNotification.notificationsTradeAlerts}
                    onChange={(v) =>
                      setSystemNotification({
                        ...systemNotification,
                        notificationsTradeAlerts: v,
                      })
                    }
                  />
                </SettingsSection>

                {/* KYC */}
                <SettingsSection title="KYC">
                  <ToggleField
                    label="Required for Trading"
                    checked={kyc.requiredForTrading}
                    onChange={(v) => setKyc({ ...kyc, requiredForTrading: v })}
                  />
                  <ToggleField
                    label="Required for Withdrawal"
                    checked={kyc.requiredForWithdrawal}
                    onChange={(v) =>
                      setKyc({ ...kyc, requiredForWithdrawal: v })
                    }
                  />
                  <ToggleField
                    label="Auto-Approve Submissions"
                    description="Automatically approve KYC submissions"
                    checked={kyc.autoApprove}
                    onChange={(v) => setKyc({ ...kyc, autoApprove: v })}
                  />
                  <ToggleField
                    label="Required for Transfer"
                    checked={kyc.requiredForTransfer}
                    onChange={(v) => setKyc({ ...kyc, requiredForTransfer: v })}
                  />
                  <ToggleField
                    label="Required for Swap"
                    checked={kyc.requiredForSwap}
                    onChange={(v) => setKyc({ ...kyc, requiredForSwap: v })}
                  />
                </SettingsSection>
                <div className="flex justify-end mt-6">
                  <Button
                    onClick={() => {
                      if (activeTab === "platform" && !general.currencyId) {
                        return toast.warning(
                          "Please select default currency to proceed.",
                        );
                      }

                      setVerificationType("admin-settings");
                      setShowVerification(true);
                    }}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Update Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <AdminVerificationDialog
        open={showVerification}
        onOpenChange={setShowVerification}
        onSuccess={async ({ otp, securityCode }) => {
          if (verificationType === "profile") {
            await makeRequest.put("/user/me", {
              ...profileBuildPayload(),
              otp,
              securityCode,
            });

            queryClient.invalidateQueries({
              queryKey: ["profile", userId],
            });

            toast.success("Profile updated");
          }

          if (verificationType === "admin-settings") {
            await mutation.mutateAsync({
              ...buildPayload(),
              otp,
              securityCode,
            });

            toast.success("Settings updated successfully");
          }

          if (verificationType === "security") {
            await makeRequest.put("/setting/me", {
              twoFAEnabled: security.twoFactor,
              pushNotifications: security.loginNotifications,
              otp,
              securityCode,
            });

            toast.success("Security settings updated");
          }

          if (verificationType === "notification") {
            await makeRequest.put("/setting/me", {
              emailAlerts: notifications.email,
              pushNotifications: notifications.push,
              newFeatureAlert: notifications.marketing,
              otp,
              securityCode,
            });

            toast.success("Notification preferences updated");
          }

          setVerificationType(null);
        }}
      />

      <TransactionPasswordSetting mode={mode} open={open} setOpen={setOpen} />
    </div>
  );
}
